import React, { useEffect, useState } from 'react';
import { Modal, ModalContentBody, Size, TypographyColor } from '@signaturit/react-components-library';
import { Link, Typography } from '@material-ui/core';
import { ASSETS_PATH, ROOT_URL } from '../../constants/endpoints';
import { useActivationEmailModalStyles } from './ActivationEmailModal.styles';
import { Trans, useTranslation } from 'react-i18next';
import { resendActivationEmail } from '../../store/Email/actions/resendActivationEmail';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserInfo } from '../../store/User/reducer';
import { LangSelector } from './LangSelector/LangSelector';
import { sendDataTrackerEventHandler } from '../../dataTracker/DataTrackerHelper';
import { DataTrackerEvents } from '../../dataTracker/DataTrackerEvents';
import { IdleStorageKey, IdleStorageValue } from '../../../constants/IdleStorage';
import { getCurrentLanguage } from '../../../helpers/LangHelper';
import { useLocalStorageEvents } from '../../../hooks/useLocalStorageEvents/useLocalStorageEvents';

export const ACTIVATION_EMAIL_MODAL_DATA_SIGNA_PREFIX = 'activation-email';

export const ActivationEmailModal = () => {
    const classes = useActivationEmailModalStyles();
    const dispatch = useDispatch();
    const user = useSelector(selectUserInfo);

    const { t } = useTranslation();
    const [canResendActivationEmail, setCanResendActivationEmail] = useState<boolean>(true);
    const emailImageSrc = `${ASSETS_PATH}/img/email-modal/email.png`;
    const { setItemStorage } = useLocalStorageEvents();

    const resendActivation = () => {
        if (canResendActivationEmail && user) {
            dispatch(resendActivationEmail(user.email));
            setCanResendActivationEmail(false);
        }
    };

    const logout = (): void => {
        setItemStorage(IdleStorageKey.TimeoutLogout, IdleStorageValue.UserIsLogout);
        window.location.href = `${ROOT_URL}/${getCurrentLanguage()}/logout`;
    };

    useEffect(() => {
        if (user) {
            sendDataTrackerEventHandler(DataTrackerEvents.SendVerifyEmailModalViewed, {}, []);
        }
    }, [user]);

    return (
        <Modal
            opened={true}
            size={Size.large}
            hideCloseButton={true}
            dataName={ACTIVATION_EMAIL_MODAL_DATA_SIGNA_PREFIX}
        >
            <ModalContentBody dataName={ACTIVATION_EMAIL_MODAL_DATA_SIGNA_PREFIX}>
                <div className={classes.langSelector}>
                    <LangSelector />
                </div>
                <div className={classes.content}>
                    <img
                        className={classes.img}
                        src={emailImageSrc}
                        alt={`${ACTIVATION_EMAIL_MODAL_DATA_SIGNA_PREFIX} img`}
                        data-signa={`${ACTIVATION_EMAIL_MODAL_DATA_SIGNA_PREFIX}-img`}
                    />
                    <Typography
                        variant="h4"
                        className={classes.title}
                        data-signa={`${ACTIVATION_EMAIL_MODAL_DATA_SIGNA_PREFIX}-title`}
                    >
                        {t('default.activation_email_modal.title')}
                    </Typography>
                    <Typography
                        variant="body2"
                        color={TypographyColor.textPrimary}
                        className={classes.body}
                        data-signa={`${ACTIVATION_EMAIL_MODAL_DATA_SIGNA_PREFIX}-body`}
                    >
                        {t('default.activation_email_modal.body', { email: user?.email })}
                    </Typography>
                    <Typography
                        variant="body2"
                        color={TypographyColor.textPrimary}
                        className={classes.body}
                        data-signa={`${ACTIVATION_EMAIL_MODAL_DATA_SIGNA_PREFIX}-actions`}
                    >
                        <Trans
                            i18nKey={'default.activation_email_modal.actions'}
                            components={[
                                <Link
                                    data-signa={`${ACTIVATION_EMAIL_MODAL_DATA_SIGNA_PREFIX}-link`}
                                    className={classes.link}
                                    underline="always"
                                    onClick={resendActivation}
                                />,
                                <Link
                                    data-signa={`${ACTIVATION_EMAIL_MODAL_DATA_SIGNA_PREFIX}-logout-link`}
                                    className={classes.link}
                                    underline="always"
                                    onClick={logout}
                                />,
                            ]}
                        />
                    </Typography>
                </div>
            </ModalContentBody>
        </Modal>
    );
};
